/**
 * Component for password input.
 *
 * Must be embedded in a react-hook-form.
 */

import React, { useState } from 'react';

import { useFormContext, Controller } from 'react-hook-form';

import {
  OutlinedInput,
  InputLabel,
  InputAdornment,
  IconButton,
  FormHelperText,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { FormControlStyled } from 'elements/mismosh';

interface Props {
  label: string;
  name?: string;
  required?: boolean;
}

export const Password: React.FC<Props> = ({
  label,
  name = 'password',
  required = true,
}) => {
  const { control, formState } = useFormContext();
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const error = formState.errors.password?.message;

  return (
    <FormControlStyled size="small">
      <InputLabel htmlFor="password">{label}</InputLabel>
      <Controller
        name={name}
        rules={{
          required: { value: required, message: 'Password is required' },
        }}
        control={control}
        render={({ field }) => (
          <>
            <OutlinedInput
              {...field}
              error={Boolean(error)}
              type={showPassword ? 'text' : 'password'}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleTogglePasswordVisibility}
                    edge="end"
                    size="large"
                  >
                    {showPassword ? (
                      <Visibility fontSize="small" />
                    ) : (
                      <VisibilityOff fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
            <FormHelperText error>{error ?? '\u00a0'}</FormHelperText>
          </>
        )}
      />
    </FormControlStyled>
  );
};
